/**
 * Widget configuration form.
 *
 * Renders only the controls the selected widget type actually uses, driven by
 * the live field list of the chosen data source. Statistics that need a numeric
 * column offer only numeric fields, so an invalid combination cannot be built
 * through the UI (and `validateWidget` still re-checks it before publishing).
 */

import {
  ACTION_KIND_LABELS,
  ACTION_MATCH_LABELS,
  type ActionMatch,
  CHART_TYPES,
  STATISTIC_LABELS,
  defaultKindsForTarget,
  kindsForTarget,
  triggerForWidgetType,
} from '../types/dashboardTypes';
import type {
  ActionFieldPair,
  ActionKind,
  DashboardDataSource,
  DashboardField,
  DashboardWidget,
  StatisticType,
  WidgetAction,
  WidgetConfig,
} from '../types/dashboardTypes';
import { Palette, Tag } from 'lucide-react';
import { numericFields, useSourceFields } from './useSourceFields';
import FilterEditor from './FilterEditor';
import Tooltip from '../../components/glass/Tooltip';

/** What each match mode means, on its chip. */
const ACTION_MATCH_HINTS: Record<ActionMatch, string> = {
  attribute:
    'Keeps rows whose column equals the selected value. Name the column on each side below — they do not have to share a name, only the values have to line up.',
  spatial:
    'Keeps features that touch or fall inside the selected shape — a neighbour sharing a boundary counts. Needs no shared column, so use this when the two layers have nothing in common but where they are.',
};

/** One line per action kind, shown on the chips in the Actions editor. */
const ACTION_KIND_HINTS: Record<ActionKind, string> = {
  filter: 'Narrows this widget to the selected value.',
  select: 'Highlights the matching features on the map.',
  zoom: 'Frames the map on the matching features.',
  pan: 'Recentres the map on them without changing the zoom.',
};

interface Props {
  widget: DashboardWidget;
  dataSources: DashboardDataSource[];
  onChange: (widget: DashboardWidget) => void;
  /** Every widget on the dashboard, so actions can pick their targets. */
  allWidgets?: DashboardWidget[];
  /**
   * Opens the Symbology or Labels editor for one of this map widget's data
   * sources. The editors themselves are floating panels that need to sit
   * over the live preview canvas, not this sidebar, so the parent owns
   * mounting them — this just asks it to.
   */
  onStyleSource?: (dataSourceId: string, mode: 'symbology' | 'labels') => void;
}

/* ─────────────────────────── small form controls ────────────────────────── */

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-0.5 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

const inputClass =
  'gl-input px-2.5 py-1.5 text-xs';

export default function WidgetConfigForm({
  widget,
  dataSources,
  onChange,
  allWidgets = [],
  onStyleSource,
}: Props) {
  const listGrouped = widget.type === 'list' && widget.config.listMode === 'grouped';
  /** Mirrors ChartWidget: unset falls back to the widget type's historic shape. */
  const chartOrientation =
    widget.config.orientation ?? (widget.type === 'column' ? 'vertical' : 'horizontal');
  const source = dataSources.find((entry) => entry.id === widget.dataSourceId);
  const { fields, loading } = useSourceFields(source);

  const set = (changes: Partial<DashboardWidget>) => onChange({ ...widget, ...changes });
  const setConfig = (changes: Partial<WidgetConfig>) =>
    onChange({ ...widget, config: { ...widget.config, ...changes } });

  const statistic = widget.config.statistic ?? 'count';
  const numeric = numericFields(fields);
  const isChart = CHART_TYPES.includes(widget.type);
  const needsSource = widget.type !== 'text' && widget.type !== 'map';

  return (
    <div className="space-y-4">
      <Field label="Title">
        <input
          value={widget.title}
          onChange={(event) => set({ title: event.target.value })}
          className={inputClass}
        />
      </Field>

      {/* ── Data source ─────────────────────────────────────────────────── */}
      {needsSource && (
        <Field
          label="Data source"
          hint={dataSources.length === 0 ? 'Add a layer in the Data Sources step first.' : undefined}
        >
          <select
            value={widget.dataSourceId ?? ''}
            onChange={(event) => {
              // Field references belong to the previous layer; clear them so a
              // stale column name cannot survive a source change.
              set({
                dataSourceId: event.target.value || undefined,
                filters: [],
                config: {
                  ...widget.config,
                  field: undefined,
                  categoryField: undefined,
                  columns: [],
                  orderByField: undefined,
                },
              });
            }}
            className={inputClass}
          >
            <option value="">Select a layer…</option>
            {dataSources.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      {needsSource && source && loading && (
        <p className="text-[11px] text-slate-400">Loading fields…</p>
      )}

      {/* ── KPI + Gauge ─────────────────────────────────────────────────── */}
      {(widget.type === 'kpi' || widget.type === 'gauge') && source && (
        <>
          <Field label="Statistic">
            <select
              value={statistic}
              onChange={(event) =>
                setConfig({ statistic: event.target.value as StatisticType })
              }
              className={inputClass}
            >
              {(Object.keys(STATISTIC_LABELS) as StatisticType[]).map((entry) => (
                <option key={entry} value={entry}>
                  {STATISTIC_LABELS[entry]}
                </option>
              ))}
            </select>
          </Field>

          {statistic !== 'count' && (
            <Field
              label="Field"
              hint={numeric.length === 0 ? 'This layer reports no numeric fields.' : undefined}
            >
              <select
                value={widget.config.field ?? ''}
                onChange={(event) => setConfig({ field: event.target.value || undefined })}
                className={inputClass}
              >
                <option value="">Select a field…</option>
                {numeric.map((field) => (
                  <option key={field.name} value={field.name}>
                    {field.alias}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </>
      )}

      {widget.type === 'kpi' && (
        <>
          <Field label="Caption">
            <input
              value={widget.config.caption ?? ''}
              onChange={(event) => setConfig({ caption: event.target.value })}
              placeholder="Optional line under the number"
              className={inputClass}
            />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Prefix">
              <input
                value={widget.config.prefix ?? ''}
                onChange={(event) => setConfig({ prefix: event.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Suffix">
              <input
                value={widget.config.suffix ?? ''}
                onChange={(event) => setConfig({ suffix: event.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Decimals">
              <input
                type="number"
                min={0}
                max={4}
                value={widget.config.decimals ?? 0}
                onChange={(event) => setConfig({ decimals: Number(event.target.value) })}
                className={inputClass}
              />
            </Field>
          </div>
        </>
      )}

      {/* ── Gauge scale ─────────────────────────────────────────────────── */}
      {widget.type === 'gauge' && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Minimum">
              <input
                type="number"
                value={widget.config.minValue ?? 0}
                onChange={(event) => setConfig({ minValue: Number(event.target.value) })}
                className={inputClass}
              />
            </Field>
            <Field label="Maximum">
              <input
                type="number"
                value={widget.config.maxValue ?? 100}
                onChange={(event) => setConfig({ maxValue: Number(event.target.value) })}
                className={inputClass}
              />
            </Field>
            <Field label="Target">
              <input
                type="number"
                value={widget.config.targetValue ?? ''}
                onChange={(event) =>
                  setConfig({
                    targetValue: event.target.value === '' ? undefined : Number(event.target.value),
                  })
                }
                placeholder="none"
                className={inputClass}
              />
            </Field>
          </div>
          <Field label="Units">
            <input
              value={widget.config.suffix ?? ''}
              onChange={(event) => setConfig({ suffix: event.target.value })}
              placeholder="e.g. km, %"
              className={inputClass}
            />
          </Field>
        </>
      )}

      {/* ── Charts ──────────────────────────────────────────────────────── */}
      {isChart && source && (
        <>
          <Field label="Category field" hint="Each distinct value becomes a slice, bar, or point.">
            <select
              value={widget.config.categoryField ?? ''}
              onChange={(event) => setConfig({ categoryField: event.target.value || undefined })}
              className={inputClass}
            >
              <option value="">Select a field…</option>
              {fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.alias}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Statistic">
            <select
              value={statistic}
              onChange={(event) => setConfig({ statistic: event.target.value as StatisticType })}
              className={inputClass}
            >
              {(Object.keys(STATISTIC_LABELS) as StatisticType[]).map((entry) => (
                <option key={entry} value={entry}>
                  {STATISTIC_LABELS[entry]}
                </option>
              ))}
            </select>
          </Field>

          {statistic !== 'count' && (
            <Field label="Value field">
              <select
                value={widget.config.field ?? ''}
                onChange={(event) => setConfig({ field: event.target.value || undefined })}
                className={inputClass}
              >
                <option value="">Select a field…</option>
                {numeric.map((field) => (
                  <option key={field.name} value={field.name}>
                    {field.alias}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Field label="Max categories">
              <input
                type="number"
                min={1}
                max={30}
                value={widget.config.maxCategories ?? 8}
                onChange={(event) => setConfig({ maxCategories: Number(event.target.value) })}
                className={inputClass}
              />
            </Field>
            {/* Bars only. A pie has no axes to turn, and a line is always
              time/category across the bottom. */}
          {(widget.type === 'bar' || widget.type === 'column') && (
            <Field label="Orientation">
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
                {([
                  { id: 'horizontal' as const, label: 'Horizontal', hint: 'Bars run left to right. Best for long category names.' },
                  { id: 'vertical' as const, label: 'Vertical', hint: 'Bars run bottom to top, like a classic column chart.' },
                ]).map((option) => (
                  <Tooltip key={option.id} content={option.hint}>
                    <button
                      type="button"
                      aria-pressed={chartOrientation === option.id}
                      onClick={() => setConfig({ orientation: option.id })}
                      className={`rounded-md px-2 py-1.5 text-[11px] font-medium transition ${
                        chartOrientation === option.id
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  </Tooltip>
                ))}
              </div>
            </Field>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Field label="X axis title" hint="Leave blank for none.">
              <input
                value={widget.config.xAxisLabel ?? ''}
                onChange={(event) => setConfig({ xAxisLabel: event.target.value })}
                placeholder="e.g. Favourite colour"
                className={inputClass}
              />
            </Field>
            <Field label="Y axis title" hint="Leave blank for none.">
              <input
                value={widget.config.yAxisLabel ?? ''}
                onChange={(event) => setConfig({ yAxisLabel: event.target.value })}
                placeholder="e.g. Children"
                className={inputClass}
              />
            </Field>
          </div>

          <Field label="Sort">
              <select
                value={widget.config.sortBy ?? 'value-desc'}
                onChange={(event) =>
                  setConfig({ sortBy: event.target.value as WidgetConfig['sortBy'] })
                }
                className={inputClass}
              >
                <option value="value-desc">Value, high to low</option>
                <option value="value-asc">Value, low to high</option>
                <option value="category-asc">Category, A–Z</option>
                <option value="category-desc">Category, Z–A</option>
              </select>
            </Field>
          </div>

          <div className="flex flex-wrap gap-3">
            {(widget.type === 'pie' || widget.type === 'donut') && (
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={widget.config.showLegend !== false}
                  onChange={(event) => setConfig({ showLegend: event.target.checked })}
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                Show legend
              </label>
            )}
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={widget.config.showValueLabels !== false}
                onChange={(event) => setConfig({ showValueLabels: event.target.checked })}
                className="h-3.5 w-3.5 accent-blue-600"
              />
              Show value labels
            </label>
          </div>
        </>
      )}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      {/* ── List ────────────────────────────────────────────────────────── */}
      {widget.type === 'list' && source && (
        <>
          <Field
            label="Show"
            hint={
              listGrouped
                ? 'Each distinct value appears once, with a statistic beside it.'
                : 'One row per record, in the order the service returns them.'
            }
          >
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
              {([
                { id: 'features' as const, label: 'Individual features' },
                { id: 'grouped' as const, label: 'Unique values' },
              ]).map((option) => (
                <Tooltip
                  key={option.id}
                  content={
                    option.id === 'grouped'
                      ? 'One row per distinct value, with a statistic beside it. Repeats collapse into a single row.'
                      : 'One row per record, in the order the service returns them.'
                  }
                >
                <button
                  type="button"
                  onClick={() => setConfig({ listMode: option.id })}
                  className={`rounded-md px-2 py-1.5 text-[11px] font-medium transition ${
                    (widget.config.listMode ?? 'features') === option.id
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {option.label}
                </button>
                </Tooltip>
              ))}
            </div>
          </Field>

          <Field
            label={listGrouped ? 'Group by' : 'Title field'}
            hint={
              listGrouped
                ? 'Blank and null values collapse into a single row.'
                : 'The main line of each row.'
            }
          >
            <select
              value={widget.config.titleField ?? ''}
              onChange={(event) => setConfig({ titleField: event.target.value })}
              className={inputClass}
            >
              <option value="">{loading ? 'Loading fields…' : '-- Select a field --'}</option>
              {fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.alias}
                </option>
              ))}
            </select>
          </Field>

          {listGrouped && (
            <>
              <Field label="Statistic">
                <select
                  value={statistic}
                  onChange={(event) =>
                    setConfig({ statistic: event.target.value as StatisticType })
                  }
                  className={inputClass}
                >
                  {(Object.keys(STATISTIC_LABELS) as StatisticType[]).map((key) => (
                    <option key={key} value={key}>
                      {STATISTIC_LABELS[key]}
                    </option>
                  ))}
                </select>
              </Field>

              {statistic !== 'count' && (
                <Field label="Statistic field" hint="Numeric columns only.">
                  <select
                    value={widget.config.field ?? ''}
                    onChange={(event) => setConfig({ field: event.target.value })}
                    className={inputClass}
                  >
                    <option value="">
                      {loading ? 'Loading fields...' : '-- Select a field --'}
                    </option>
                    {numeric.map((field) => (
                      <option key={field.name} value={field.name}>
                        {field.alias}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <Field label="Sort">
                <select
                  value={widget.config.sortBy ?? 'value-desc'}
                  onChange={(event) =>
                    setConfig({ sortBy: event.target.value as WidgetConfig['sortBy'] })
                  }
                  className={inputClass}
                >
                  <option value="value-desc">Highest first</option>
                  <option value="value-asc">Lowest first</option>
                  <option value="category-asc">Name A-Z</option>
                  <option value="category-desc">Name Z-A</option>
                </select>
              </Field>
            </>
          )}

          {/* Per-FEATURE lines only. One group holds many different subtitle and
              value entries, so offering these in grouped mode would be a control
              the data cannot honour. */}
          {!listGrouped && (
            <>
          <Field label="Subtitle field" hint="Optional second line.">
            <select
              value={widget.config.subtitleField ?? ''}
              onChange={(event) => setConfig({ subtitleField: event.target.value })}
              className={inputClass}
            >
              <option value="">None</option>
              {fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.alias}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Value field" hint="Optional, pinned to the right of the row.">
            <select
              value={widget.config.listValueField ?? ''}
              onChange={(event) => setConfig({ listValueField: event.target.value })}
              className={inputClass}
            >
              <option value="">None</option>
              {fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.alias}
                </option>
              ))}
            </select>
          </Field>

            </>
          )}

          <Field
            label="Colour by"
            hint={
              listGrouped
                ? 'Each distinct value gets its own dot colour.'
                : 'Colours the dot at the start of each row.'
            }
          >
            <select
              value={
                listGrouped ? widget.config.titleField ?? '' : widget.config.listColorField ?? ''
              }
              disabled={listGrouped}
              onChange={(event) => setConfig({ listColorField: event.target.value })}
              className={`${inputClass} disabled:opacity-60`}
            >
              <option value="">Accent colour</option>
              {fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {field.alias}
                </option>
              ))}
            </select>
          </Field>

          <div className={listGrouped ? '' : 'grid grid-cols-2 gap-2'}>
            <Field label={listGrouped ? 'Max values' : 'Max items'}>
              <input
                type="number"
                min={1}
                max={200}
                value={widget.config.maxItems ?? 50}
                onChange={(event) => setConfig({ maxItems: Number(event.target.value) })}
                className={inputClass}
              />
            </Field>
            {!listGrouped && (
            <Field label="Sort by">
              <select
                value={widget.config.orderByField ?? ''}
                onChange={(event) => setConfig({ orderByField: event.target.value })}
                className={inputClass}
              >
                <option value="">Service order</option>
                {fields.map((field) => (
                  <option key={field.name} value={field.name}>
                    {field.alias}
                  </option>
                ))}
              </select>
            </Field>
            )}
          </div>

          {!listGrouped && widget.config.orderByField && (
            <Field label="Direction">
              <select
                value={widget.config.orderDirection ?? 'asc'}
                onChange={(event) =>
                  setConfig({ orderDirection: event.target.value as 'asc' | 'desc' })
                }
                className={inputClass}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </Field>
          )}

          <div className="flex flex-wrap gap-3 border-t border-slate-100 pt-3">
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={Boolean(widget.config.multiSelect)}
                onChange={(event) => setConfig({ multiSelect: event.target.checked })}
                className="h-3.5 w-3.5 accent-blue-600"
              />
              Allow selecting more than one
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={Boolean(widget.config.listSearch)}
                onChange={(event) => setConfig({ listSearch: event.target.checked })}
                className="h-3.5 w-3.5 accent-blue-600"
              />
              Show search box
            </label>
          </div>
        </>
      )}

      {widget.type === 'table' && source && (
        <>
          <Field label="Columns" hint="Shown in the order they are ticked.">
            <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
              {fields.length === 0 && (
                <p className="text-[11px] text-slate-400">No fields reported by this layer.</p>
              )}
              {fields.map((field) => {
                const checked = widget.config.columns?.includes(field.name) ?? false;
                return (
                  <label key={field.name} className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(event) => {
                        const current = widget.config.columns ?? [];
                        setConfig({
                          columns: event.target.checked
                            ? [...current, field.name]
                            : current.filter((name) => name !== field.name),
                        });
                      }}
                      className="h-3.5 w-3.5 accent-blue-600"
                    />
                    <span className="truncate">{field.alias}</span>
                  </label>
                );
              })}
            </div>
          </Field>

          <div className="grid grid-cols-3 gap-2">
            <Field label="Sort by">
              <select
                value={widget.config.orderByField ?? ''}
                onChange={(event) => setConfig({ orderByField: event.target.value || undefined })}
                className={inputClass}
              >
                <option value="">None</option>
                {fields.map((field) => (
                  <option key={field.name} value={field.name}>
                    {field.alias}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Direction">
              <select
                value={widget.config.orderDirection ?? 'asc'}
                onChange={(event) =>
                  setConfig({ orderDirection: event.target.value as 'asc' | 'desc' })
                }
                className={inputClass}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </Field>
            <Field label="Page size">
              <input
                type="number"
                min={1}
                max={100}
                value={widget.config.pageSize ?? 10}
                onChange={(event) => setConfig({ pageSize: Number(event.target.value) })}
                className={inputClass}
              />
            </Field>
          </div>
        </>
      )}

      {/* ── Map ─────────────────────────────────────────────────────────── */}
      {widget.type === 'map' && (
        <>
          <Field label="Layers" hint="Drawn using the dashboard’s own data sources.">
            <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
              {dataSources.length === 0 && (
                <p className="text-[11px] text-slate-400">No data sources added yet.</p>
              )}
              {dataSources.map((entry) => {
                const checked = widget.config.mapDataSourceIds?.includes(entry.id) ?? false;
                return (
                  <div key={entry.id} className="flex items-center gap-2 text-xs text-slate-700">
                    <label className="flex min-w-0 flex-1 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          const current = widget.config.mapDataSourceIds ?? [];
                          setConfig({
                            mapDataSourceIds: event.target.checked
                              ? [...current, entry.id]
                              : current.filter((id) => id !== entry.id),
                          });
                        }}
                        className="h-3.5 w-3.5 accent-blue-600"
                      />
                      <span className="truncate">{entry.name}</span>
                    </label>
                    {checked && onStyleSource && (
                      <div className="flex flex-shrink-0 items-center gap-0.5">
                        <Tooltip content="Symbology" placement="top">
                          <button
                            type="button"
                            onClick={() => onStyleSource(entry.id, 'symbology')}
                            aria-label={`Symbology for ${entry.name}`}
                            className="rounded p-1 text-slate-400 transition hover:bg-white hover:text-blue-600"
                          >
                            <Palette className="h-3.5 w-3.5" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Labels" placement="top">
                          <button
                            type="button"
                            onClick={() => onStyleSource(entry.id, 'labels')}
                            aria-label={`Labels for ${entry.name}`}
                            className="rounded p-1 text-slate-400 transition hover:bg-white hover:text-blue-600"
                          >
                            <Tag className="h-3.5 w-3.5" />
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Field>

          <Field label="Basemap">
            <select
              value={widget.config.basemap ?? 'street'}
              onChange={(event) =>
                setConfig({ basemap: event.target.value as WidgetConfig['basemap'] })
              }
              className={inputClass}
            >
              <option value="street">Street</option>
              <option value="terrain">Terrain</option>
              <option value="satellite">Satellite</option>
              <option value="dark">Dark canvas</option>
            </select>
          </Field>
        </>
      )}

      {/* ── Text ────────────────────────────────────────────────────────── */}
      {widget.type === 'text' && (
        <>
          <Field label="Heading">
            <input
              value={widget.config.heading ?? ''}
              onChange={(event) => setConfig({ heading: event.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label="Body">
            <textarea
              value={widget.config.body ?? ''}
              onChange={(event) => setConfig({ body: event.target.value })}
              rows={4}
              className={`${inputClass} resize-none`}
            />
          </Field>
          <Field label="Alignment">
            <select
              value={widget.config.align ?? 'left'}
              onChange={(event) => setConfig({ align: event.target.value as WidgetConfig['align'] })}
              className={inputClass}
            >
              <option value="left">Left</option>
              <option value="center">Centre</option>
              <option value="right">Right</option>
            </select>
          </Field>
        </>
      )}

      {/* ── Colour ──────────────────────────────────────────────────────── */}
      {widget.type !== 'table' && widget.type !== 'map' && (
        <Field label="Colour" hint="Leave unset to follow the dashboard accent.">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={widget.config.color ?? '#2563EB'}
              onChange={(event) => setConfig({ color: event.target.value })}
              aria-label="Widget colour"
              className="h-7 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
            />
            {widget.config.color && (
              <button
                type="button"
                onClick={() => setConfig({ color: undefined })}
                className="text-[11px] font-medium text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline"
              >
                Reset
              </button>
            )}
          </div>
        </Field>
      )}

      {/* ── Outline ─────────────────────────────────────────────────────── */}
      {/* The widget's own BOX border — every type has one of these, unlike
          "Colour" above which tints only a KPI number or a chart, so this
          isn't gated by widget type. */}
      <Field label="Outline colour" hint="Leave unset to keep the theme's own hairline border.">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={widget.config.outlineColor ?? '#2563EB'}
            onChange={(event) => setConfig({ outlineColor: event.target.value })}
            aria-label="Widget outline colour"
            className="h-7 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
          />
          {widget.config.outlineColor && (
            <button
              type="button"
              onClick={() => setConfig({ outlineColor: undefined })}
              className="text-[11px] font-medium text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline"
            >
              Reset
            </button>
          )}
        </div>
      </Field>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      {needsSource && source && (
        <div className="border-t border-slate-100 pt-3">
          <FilterEditor
            filters={widget.filters}
            fields={fields}
            source={source}
            onChange={(filters) => set({ filters })}
          />
        </div>
      )}

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <ActionsEditor
        widget={widget}
        allWidgets={allWidgets}
        dataSources={dataSources}
        sourceFields={fields}
        onChange={onChange}
      />
    </div>
  );
}

/**
 * One target in the Actions editor: what it reacts to, and how it is matched.
 *
 * A component of its own because it has to load the TARGET layer's columns to
 * offer them, and a hook cannot be called from inside a `map`. It also keeps
 * the loading and empty states local, so one slow layer does not hold up the
 * other rows.
 */
function TargetRow({
  target,
  targetSource,
  sourceFields,
  broadcastField,
  kinds,
  match,
  pair,
  onKinds,
  onMatch,
  onPair,
}: {
  target: DashboardWidget;
  targetSource: DashboardDataSource | undefined;
  sourceFields: DashboardField[];
  broadcastField: string;
  kinds: ActionKind[];
  match: ActionMatch;
  pair: ActionFieldPair;
  onKinds: (kinds: ActionKind[]) => void;
  onMatch: (mode: ActionMatch) => void;
  onPair: (changes: Partial<ActionFieldPair>) => void;
}) {
  const { fields: targetFields, loading: targetLoading } = useSourceFields(targetSource);

  const available = kindsForTarget(target.type);
  const on = kinds.length > 0;

  /* A map is only ever matched by attribute — it has no Filter chip to hang a
     mode off, and "highlight what is inside this shape" is a different feature
     from "highlight the same asset id". Everything else shows the pairing only
     while it is actually filtering by attribute. */
  const pairing = on && (kinds.includes('filter') ? match === 'attribute' : target.type === 'map');

  const sourceField = pair.source || broadcastField;
  const targetField = pair.target || sourceField;

  const fieldOptions = (list: DashboardField[], selected: string) => {
    const known = list.some((entry) => entry.name === selected);
    return (
      <>
        {/* A field saved earlier that the layer no longer publishes is still
            shown, or switching layers would silently reset the pairing to
            something the admin never chose. */}
        {selected && !known && <option value={selected}>{selected} (not in this layer)</option>}
        {list.map((entry) => (
          <option key={entry.name} value={entry.name}>
            {entry.alias && entry.alias !== entry.name
              ? `${entry.alias} (${entry.name})`
              : entry.name}
          </option>
        ))}
      </>
    );
  };

  const selectClass =
    'min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-700';

  /** What this pairing will actually do, in the admin's own field names. */
  const explain = () => {
    if (targetLoading) return 'Loading this widget’s columns…';
    if (targetFields.length === 0) {
      return 'This widget has no data source yet, so there are no columns to match against.';
    }
    const kept = target.type === 'map' ? 'Highlights features' : 'Keeps rows';
    if (sourceField !== broadcastField) {
      return `Looks up ${sourceField} on the selected rows, then matches it against ${targetField}. Costs one extra request per selection.`;
    }
    if (targetField !== sourceField) {
      return `${kept} where ${targetField} equals the selected ${sourceField}.`;
    }
    return 'Both sides use the same column, which is the default.';
  };

  return (
    <li className="rounded-lg border border-slate-200 px-2 py-1.5">
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={on}
          onChange={() => onKinds(on ? [] : defaultKindsForTarget(target.type))}
          className="h-3.5 w-3.5 accent-blue-600"
        />
        <span className="min-w-0 flex-1 truncate text-xs text-slate-700">{target.title}</span>
        <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
          {target.type}
        </span>
      </label>

      {on && (
        <div className="mt-1.5 flex flex-wrap gap-1 pl-6">
          {available.map((kind) => {
            const enabled = kinds.includes(kind);
            return (
              <Tooltip key={kind} content={ACTION_KIND_HINTS[kind]}>
                <button
                  type="button"
                  aria-pressed={enabled}
                  onClick={() =>
                    onKinds(enabled ? kinds.filter((entry) => entry !== kind) : [...kinds, kind])
                  }
                  className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition ${
                    enabled
                      ? 'border-blue-400 bg-blue-50 text-blue-700'
                      : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {ACTION_KIND_LABELS[kind]}
                </button>
              </Tooltip>
            );
          })}
        </div>
      )}

      {/* How a Filter matches, offered only when Filter is on. By attribute
          compares a column on each side; by location needs no shared column at
          all - a ward polygon can filter a pipe layer that has never heard of
          wards. Which one is meant cannot be read off the data, so it is
          asked. */}
      {on && kinds.includes('filter') && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1 pl-6">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Match</span>
          {(['attribute', 'spatial'] as ActionMatch[]).map((mode) => (
            <Tooltip key={mode} content={ACTION_MATCH_HINTS[mode]}>
              <button
                type="button"
                aria-pressed={match === mode}
                onClick={() => onMatch(mode)}
                className={`rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition ${
                  match === mode
                    ? 'border-blue-400 bg-blue-50 text-blue-700'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
              >
                {ACTION_MATCH_LABELS[mode]}
              </button>
            </Tooltip>
          ))}
        </div>
      )}

      {/* The pairing. Both sides are named because two layers routinely spell
          the same fact differently - WARD_NO here, WARDNO there - and only the
          person who knows the data can say which columns line up. */}
      {pairing && (
        <div className="mt-1.5 space-y-1 rounded-md bg-slate-50 px-2 py-1.5">
          <div className="flex items-center gap-1.5">
            <span className="w-14 flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
              From
            </span>
            <select
              value={sourceField}
              onChange={(event) => onPair({ source: event.target.value })}
              className={selectClass}
            >
              {fieldOptions(sourceFields, sourceField)}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="w-14 flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
              Matches
            </span>
            <select
              value={targetField}
              onChange={(event) => onPair({ target: event.target.value })}
              disabled={targetLoading || targetFields.length === 0}
              className={`${selectClass} disabled:bg-slate-100 disabled:text-slate-400`}
            >
              {fieldOptions(targetFields, targetField)}
            </select>
          </div>

          <p className="text-[10px] leading-relaxed text-slate-400">{explain()}</p>
        </div>
      )}
    </li>
  );
}

/**
 * The field a source widget puts on the wire when something is selected.
 *
 * Mirrors what each renderer actually does — the list broadcasts its title
 * field, the chart its category, the table its first column — so the Actions
 * editor can show the admin what they are starting from rather than making
 * them remember. Kept in step with ListWidget, ChartWidget and TableWidget.
 */
function broadcastFieldOf(widget: DashboardWidget): string {
  if (widget.type === 'table') return widget.config.columns?.[0] ?? '';
  if (widget.type === 'list') return widget.config.titleField ?? '';
  return widget.config.categoryField ?? '';
}

/**
 * What this widget does to the others when the user interacts with it.
 *
 * Shown only for widget types that can raise a trigger — a KPI has nothing to
 * click, so offering it an action would be a promise the runtime cannot keep.
 *
 * Kinds are per TARGET, not per source: filtering a chart and zooming a map are
 * different things to ask of different tiles, and Esri configures them the same
 * way. A target is stored as one `WidgetAction` per kind, so the schema needs
 * no nesting.
 */
function ActionsEditor({
  widget,
  allWidgets,
  dataSources,
  sourceFields,
  onChange,
}: {
  widget: DashboardWidget;
  allWidgets: DashboardWidget[];
  dataSources: DashboardDataSource[];
  /** Fields of the SOURCE widget's layer, already loaded by the parent form. */
  sourceFields: DashboardField[];
  onChange: (widget: DashboardWidget) => void;
}) {
  const trigger = triggerForWidgetType(widget.type);
  if (!trigger) return null;

  const actions = widget.actions ?? [];

  /** Kinds currently enabled for one target. */
  const kindsFor = (targetId: string): ActionKind[] =>
    actions
      .filter((action) => action.targetWidgetIds.includes(targetId))
      .map((action) => action.kind);

  /** Rewrites the action list so `targetId` has exactly `kinds`. */
  /**
   * An action with `targetId` removed, and its pairing removed with it.
   *
   * The two have to travel together. Leaving the entry behind would attach a
   * stale mapping to whatever target is wired up next under the same id, and
   * dropping it silently would lose a pairing the admin set when they only
   * meant to flip a switch — so the caller is handed the entry back to carry.
   */
  const withoutTarget = (
    action: WidgetAction,
    targetId: string,
  ): { rest: WidgetAction | null; pair?: ActionFieldPair } => {
    const pair = action.fieldMap?.[targetId];
    const others = action.targetWidgetIds.filter((id) => id !== targetId);
    if (others.length === 0) return { rest: null, pair };

    const fieldMap = { ...(action.fieldMap ?? {}) };
    delete fieldMap[targetId];
    return {
      rest: {
        ...action,
        targetWidgetIds: others,
        ...(Object.keys(fieldMap).length > 0 ? { fieldMap } : { fieldMap: undefined }),
      },
      pair,
    };
  };

  const setKinds = (targetId: string, kinds: ActionKind[]) => {
    const next: WidgetAction[] = [];
    let carried: ActionFieldPair | undefined;

    // Keep every other target's wiring untouched.
    for (const action of actions) {
      const { rest, pair } = withoutTarget(action, targetId);
      if (pair) carried = pair;
      if (rest) next.push(rest);
    }

    for (const kind of kinds) {
      /* A filter has two flavours, so a target being switched on joins the
         attribute one — the default a fresh target should get. Matching on
         kind alone would drop it into a spatial action that happens to sit
         first in the list, giving it a mode nobody chose for it. */
      const existing = next.find(
        (action) =>
          action.kind === kind &&
          (kind !== 'filter' || (action.match ?? 'attribute') === 'attribute'),
      );
      const pairing = carried ? { [targetId]: carried } : undefined;

      if (existing) {
        existing.targetWidgetIds = [...existing.targetWidgetIds, targetId];
        if (pairing) existing.fieldMap = { ...(existing.fieldMap ?? {}), ...pairing };
      } else
        next.push({
          id: `${widget.id}-${kind}`,
          trigger,
          kind,
          targetWidgetIds: [targetId],
          ...(pairing ? { fieldMap: pairing } : null),
        });
    }

    onChange({ ...widget, actions: next.filter((action) => action.targetWidgetIds.length > 0) });
  };

  /** How this target's Filter matches. Absent means by attribute. */
  const matchFor = (targetId: string): ActionMatch =>
    (widget.actions ?? []).find(
      (action) => action.kind === 'filter' && action.targetWidgetIds.includes(targetId),
    )?.match ?? 'attribute';

  const setMatch = (targetId: string, mode: ActionMatch) => {
    /* Splits the action when only SOME of its targets change mode: one action
       carries one match, so a target moving to a different mode moves to its
       own action rather than dragging the others with it. */
    const next: WidgetAction[] = [];
    /* The pairing rides along. By location ignores it, but flipping there and
       back is one click, and losing the columns the admin chose to a mode
       toggle would be a small betrayal every time. */
    let carried: ActionFieldPair | undefined;

    for (const action of widget.actions ?? []) {
      if (action.kind !== 'filter' || !action.targetWidgetIds.includes(targetId)) {
        next.push(action);
        continue;
      }
      const { rest, pair } = withoutTarget(action, targetId);
      if (pair) carried = pair;
      if (rest) next.push(rest);
    }

    const pairing = carried ? { [targetId]: carried } : undefined;
    const sibling = next.find((action) => action.kind === 'filter' && (action.match ?? 'attribute') === mode);
    if (sibling) {
      sibling.targetWidgetIds = [...sibling.targetWidgetIds, targetId];
      if (pairing) sibling.fieldMap = { ...(sibling.fieldMap ?? {}), ...pairing };
    } else {
      next.push({
        // Its own id, so the two match modes stay separate actions.
        id: `${widget.id}-filter-${mode}`,
        trigger,
        kind: 'filter',
        match: mode,
        targetWidgetIds: [targetId],
        ...(pairing ? { fieldMap: pairing } : null),
      });
    }

    onChange({ ...widget, actions: next.filter((action) => action.targetWidgetIds.length > 0) });
  };

  /**
   * The field pairing for one target. Absent sides mean "the same field".
   *
   * Any kind, not just `filter`: highlighting matching features on a map is
   * the same question asked of a different widget, and a map layer is just as
   * likely to spell the shared column differently.
   */
  const pairFor = (targetId: string): ActionFieldPair =>
    (widget.actions ?? []).find(
      (action) => action.targetWidgetIds.includes(targetId) && action.fieldMap?.[targetId],
    )?.fieldMap?.[targetId] ?? {};

  const setPair = (targetId: string, changes: Partial<ActionFieldPair>) => {
    /* Written to EVERY action reaching this target. A map driven by both
       Select and Zoom is two actions, and they must agree on which column
       identifies the feature or the map would frame one set and highlight
       another. */
    const next = (widget.actions ?? []).map((action) => {
      if (!action.targetWidgetIds.includes(targetId)) return action;
      const merged = { ...(action.fieldMap?.[targetId] ?? {}), ...changes };
      /* An empty pair is deleted rather than stored as `{}`: "unset" and "set
         to nothing" behave identically at runtime, and keeping only one of
         them means a dashboard saved before pairing existed and one reset back
         to the defaults are the same document. */
      const cleaned: ActionFieldPair = {};
      if (merged.source) cleaned.source = merged.source;
      if (merged.target) cleaned.target = merged.target;

      const fieldMap = { ...(action.fieldMap ?? {}) };
      if (cleaned.source || cleaned.target) fieldMap[targetId] = cleaned;
      else delete fieldMap[targetId];

      return Object.keys(fieldMap).length > 0
        ? { ...action, fieldMap }
        : { ...action, fieldMap: undefined };
    });

    onChange({ ...widget, actions: next });
  };

  // Text widgets query nothing and draw nothing, so they can never be a target.
  const candidates = allWidgets.filter(
    (entry) => entry.id !== widget.id && kindsForTarget(entry.type).length > 0,
  );

  return (
    <div className="border-t border-slate-100 pt-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Actions</p>
      <p className="mt-0.5 text-[11px] leading-relaxed text-slate-400">
        {trigger === 'extent'
          ? 'Panning or zooming this map drives the widgets ticked below.'
          : 'Selecting a value here drives the widgets ticked below. Pick the same value again to clear.'}
      </p>

      {candidates.length === 0 ? (
        <p className="mt-2 text-[11px] text-slate-400">
          Add another widget to have something to drive.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {candidates.map((entry) => (
            <TargetRow
              key={entry.id}
              target={entry}
              targetSource={dataSources.find((item) => item.id === entry.dataSourceId)}
              sourceFields={sourceFields}
              broadcastField={broadcastFieldOf(widget)}
              kinds={kindsFor(entry.id)}
              match={matchFor(entry.id)}
              pair={pairFor(entry.id)}
              onKinds={(next) => setKinds(entry.id, next)}
              onMatch={(mode) => setMatch(entry.id, mode)}
              onPair={(changes) => setPair(entry.id, changes)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
